import React, { useState, useContext, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { NotificationManager } from 'react-notifications';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiTrash2, FiClock } from 'react-icons/fi';
import { FaRoute, FaCalendarAlt } from 'react-icons/fa';
import moment from 'moment';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';

export default function Ruta() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const [creating] = useState(id === 'create');
    const [route, setRoute] = useState({
        id: '',
        name: '',
        weekday: 1,
        departure: '10:00',
        direction: 'GO',
        stops: []
    });
    const [isLoading, setIsLoading] = useState(id !== 'create');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Load route
    const load = () => {
        setIsLoading(true);
        tre.api('GET', `/routes/${id}`, {
            context: context,
            success: (r) => {
                if (r.departure) r.departure = moment(r.departure, 'HH:mm:ss').format('H:mm');
                setRoute({ ...route, ...r });
                setIsLoading(false);
            },
            error: () => setIsLoading(false)
        });
    };

    useEffect(() => {
        if (!creating) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creating]);

    const handleForm = (ev) => {
        setRoute({ ...route, [ev.target.name]: ev.target.value });
    };

    const save = () => {
        setIsSaving(true);
        if (creating) {
            tre.api('POST', `/companies/${context.user.company_id}/routes`, {
                context: context,
                params: route,
                success: () => {
                    NotificationManager.success('Creada nueva ruta');
                    setIsSaving(false);
                    navigate('/routes');
                },
                error: () => setIsSaving(false)
            });
        } else {
            tre.api('PUT', `/routes/${route.id}`, {
                context: context,
                params: route,
                success: () => {
                    NotificationManager.success('Ruta modificada');
                    setIsSaving(false);
                },
                error: () => setIsSaving(false)
            });
        }
    };

    const remove = () => {
        if (!window.confirm('¿Estás seguro de eliminar esta ruta?')) return;
        setIsDeleting(true);
        tre.api('DELETE', `/routes/${route.id}`, {
            context: context,
            success: () => {
                NotificationManager.success('Ruta eliminada');
                navigate('/routes');
            },
            error: () => setIsDeleting(false)
        });
    };

    const weekdays = [
        { value: 1, label: 'Lunes' },
        { value: 2, label: 'Martes' },
        { value: 3, label: 'Miércoles' },
        { value: 4, label: 'Jueves' },
        { value: 5, label: 'Viernes' },
        { value: 6, label: 'Sábado' },
        { value: 0, label: 'Domingo' }
    ];

    // -----| Render |-----
    if (isLoading) {
        return (
            <div className="container py-4" style={{ maxWidth: '800px' }}>
                <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: '#1a73e8' }} />
                    <p className="mt-3 text-muted">Cargando ruta...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4" style={{ maxWidth: '800px' }}>
            {/* Header */}
            <div className="d-flex align-items-center mb-4">
                <Button
                    variant="link"
                    onClick={() => navigate('/routes')}
                    className="p-0 me-3"
                    style={{ color: '#5f6368' }}
                >
                    <FiArrowLeft size={24} />
                </Button>
                <div>
                    <h4 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>
                        <FaRoute className="me-2" style={{ color: '#1a73e8' }} />
                        {creating ? 'Nueva Ruta' : 'Editar Ruta'}
                    </h4>
                    {!creating && route.name && (
                        <small className="text-muted">{route.name}</small>
                    )}
                </div>
            </div>

            {/* Basic Info Card */}
            <Card className="mb-4">
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#e8f0fe',
                                color: '#1a73e8'
                            }}
                        >
                            <FaRoute size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Información de la Ruta</h6>
                            <small className="text-muted">Nombre y dirección</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <Row className="g-3">
                        <Col md={8}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Nombre de la ruta</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={route.name || ''}
                                    onChange={handleForm}
                                    placeholder="Ej: Ruta mañana Trebujena"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Dirección</Form.Label>
                                <Form.Select
                                    name="direction"
                                    value={route.direction || 'GO'}
                                    onChange={handleForm}
                                >
                                    <option value="GO">Ida</option>
                                    <option value="COME">Vuelta</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Schedule Card */}
            <Card className="mb-4">
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#e6f4ea',
                                color: '#34a853'
                            }}
                        >
                            <FaCalendarAlt size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Horario</h6>
                            <small className="text-muted">Día y hora de salida</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Día de la semana</Form.Label>
                                <Form.Select
                                    name="weekday"
                                    value={route.weekday || 1}
                                    onChange={handleForm}
                                >
                                    {weekdays.map(day => (
                                        <option key={day.value} value={day.value}>{day.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>
                                    <FiClock className="me-1" />
                                    Hora de salida
                                </Form.Label>
                                <Form.Control
                                    type="time"
                                    name="departure"
                                    value={route.departure || ''}
                                    onChange={handleForm}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Actions */}
            <div className="d-flex justify-content-between">
                <Button
                    variant="light"
                    onClick={() => navigate('/routes')}
                    style={{ padding: '10px 24px' }}
                >
                    Cancelar
                </Button>
                <div className="d-flex gap-2">
                    {!creating && (
                        <Button
                            variant="outline-danger"
                            onClick={remove}
                            disabled={isDeleting}
                            style={{ padding: '10px 24px' }}
                        >
                            {isDeleting ? <Spinner size="sm" className="me-2" /> : <FiTrash2 className="me-2" />}
                            Eliminar
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        onClick={save}
                        disabled={isSaving}
                        style={{ padding: '10px 24px' }}
                    >
                        {isSaving ? <Spinner size="sm" className="me-2" /> : <FiSave className="me-2" />}
                        {creating ? 'Crear Ruta' : 'Guardar Cambios'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
